import { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";

export default function Login() {
  const { login, fmtErr } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("desactivado") === "1") {
      setErr(t("login.deactivated_msg"));
      // limpia el query param de la url
      window.history.replaceState({}, "", "/login");
    }
  }, [t]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password.trim());
      if (!u || typeof u !== "object") {
        setErr(t("login.invalid") || "Respuesta inesperada del servidor. Inténtalo de nuevo.");
        return;
      }
      // Redirect to /plan if ?next=plan (coming from the pricing page)
      const nextParam = new URLSearchParams(window.location.search).get("next");
      if (nextParam === "plan" && u.role === "admin") {
        nav("/plan");
      } else {
        nav(u.role === "admin" ? "/" : "/trabajos");
      }
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  const LangSwitch = ({ className = "" }) => (
    <div className={`inline-flex bg-white/90 backdrop-blur rounded-full p-1 border border-slate-200 shadow-sm ${className}`} data-testid="login-lang-switch">
      <button
        type="button"
        onClick={() => setLang("es")}
        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "es" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
        data-testid="login-lang-es"
      >
        🇪🇸 ES
      </button>
      <button
        type="button"
        onClick={() => setLang("en")}
        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${lang === "en" ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
        data-testid="login-lang-en"
      >
        🇬🇧 EN
      </button>
    </div>
  );

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* Left visual */}
      <div
        className="hidden md:flex flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0B1220 0%, #142238 60%, #1D3557 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-20 rounded-2xl shadow-lg shadow-black/30" data-testid="login-logo-desktop" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-300 mb-3">{t("login.hero_tag")}</div>
          <h1 className="text-5xl font-extrabold leading-tight mb-4 whitespace-pre-line">
            {t("login.hero_title")}
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            {t("login.hero_desc")}
          </p>
        </div>
        <div className="text-white/50 text-sm">© Smile & Shine Cleaning</div>
        <div
          className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #00B4D8 0%, transparent 70%)" }}
        />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-[#F7F8FA] relative">
        {/* Idioma — visible en TODAS las pantallas, esquina superior derecha */}
        <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
          <LangSwitch />
        </div>

        <div className="w-full max-w-md sns-fade-up">
          <div className="md:hidden flex justify-center mb-6">
            <img src="/images/logo-brand.png" alt="Smile & Shine" className="h-28 rounded-3xl shadow-lg shadow-slate-300/50" data-testid="login-logo-mobile" />
          </div>
          <h2 className="sns-title text-3xl md:text-4xl mb-2">{t("login.welcome")}</h2>
          <p className="text-slate-500 mb-8">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-semibold mb-2">{t("login.email")}</label>
              <input
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                className="sns-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold">{t("login.password")}</label>
                <a href="/forgot-password" className="text-xs font-semibold text-[var(--coral)] hover:underline" data-testid="login-forgot-link">
                  {t("login.forgot_password")}
                </a>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  className="sns-input pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label={showPassword ? t("login.hide_password") : t("login.show_password")}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="sns-btn sns-btn-accent w-full text-base py-4 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? t("login.submitting") : t("login.submit")}
            </button>

            <div className="text-center text-sm text-slate-500 pt-2">
              {lang === "en" ? "New here?" : "¿Nueva empresa?"}{" "}
              <a href="/register" className="font-bold text-[var(--coral)] hover:underline" data-testid="login-register-link">
                {lang === "en" ? "Create a free account →" : "Crea tu cuenta gratis →"}
              </a>
            </div>

            <div className="text-center text-sm text-slate-500">
              {lang === "en" ? "Employee with a company code?" : "¿Empleado con código de empresa?"}{" "}
              <a href="/unirse" className="font-bold text-sky-700 hover:underline" data-testid="login-join-link">
                {lang === "en" ? "Join your company →" : "Únete a tu empresa →"}
              </a>
            </div>

            <div className="text-xs text-slate-400 text-center pt-4 border-t border-slate-100 mt-6">
              {t("login.demo_version")}
              <div className="mt-2 flex items-center justify-center gap-3">
                <a href="/pro" className="text-slate-500 hover:text-[var(--coral)] underline" data-testid="login-plans-link">
                  {lang === "en" ? "See plans" : "Ver planes"}
                </a>
                <span className="text-slate-300">·</span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[var(--coral)] underline" data-testid="login-privacy-link">
                  {lang === "en" ? "Privacy Policy" : "Política de privacidad"}
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
